import React, { useEffect, useState } from "react";
import api, { resolveAsset } from "../lib/api";
import { Download, Maximize2, X } from "lucide-react";

const typeColor = {
  event: "bg-light-blue text-brand-blue",
  holiday: "bg-soft-orange text-[color:var(--text-primary)]",
  celebration: "bg-brand-yellow/40 text-[color:var(--text-primary)]",
  activity: "bg-light-green text-[#1d6b32]",
};

const CalendarPage = () => {
  const [items, setItems] = useState([]);
  const [poster, setPoster] = useState(null);
  const [showPoster, setShowPoster] = useState(false);

  useEffect(() => {
    api.get("/calendar").then((r) => setItems(r.data));
    api.get("/calendar-posters", { params: { active_only: true } })
      .then((r) => setPoster(r.data[0] || null))
      .catch(() => {});
  }, []);

  const posterSrc = poster ? resolveAsset(poster.image_url) : "";

  return (
    <main className="pt-32 pb-24 min-h-screen" data-testid="calendar-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-brand-blue font-heading font-bold tracking-wider uppercase text-xs">Academic calendar</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-2">Term events &amp; holidays</h1>
          <p className="mt-3 text-[color:var(--text-secondary)]">
            All the dates you need to plan the year ahead.
          </p>
        </div>

        {/* Annual printable calendar */}
        {poster && (
          <section className="mb-14" data-testid="calendar-poster-section">
            <div className="card-soft bg-white overflow-hidden">
              <div className="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-[#eee]">
                <div>
                  <span className="text-brand-blue font-heading font-bold tracking-wider uppercase text-xs">Annual calendar</span>
                  <h2 className="font-heading text-2xl font-bold mt-1" data-testid="calendar-poster-label">{poster.label}</h2>
                  {poster.year && <p className="text-sm text-[color:var(--text-secondary)] mt-1">{poster.year}</p>}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => setShowPoster(true)} className="btn-ghost !py-2 !px-4 !text-sm" data-testid="calendar-poster-view">
                    <Maximize2 size={14} /> View full size
                  </button>
                  <a
                    href={posterSrc}
                    download={`${poster.label.replace(/\s+/g, "_")}.jpg`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary !py-2 !px-4 !text-sm"
                    data-testid="calendar-poster-download"
                  >
                    <Download size={14} /> Download
                  </a>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowPoster(true)}
                className="block w-full bg-cream"
                data-testid="calendar-poster-thumb"
                aria-label="Open full-size calendar"
              >
                <img
                  src={posterSrc}
                  alt={poster.label}
                  className="w-full h-auto max-h-[640px] object-contain mx-auto"
                  loading="lazy"
                />
              </button>
            </div>
          </section>
        )}

        {/* Upcoming events list */}
        <h2 className="font-heading text-2xl font-bold mb-5">All events</h2>
        {items.length === 0 ? (
          <div className="text-center text-[color:var(--text-secondary)]">No events yet.</div>
        ) : (
          <ul className="space-y-4">
            {items.map((e, i) => {
              const d = new Date(e.date);
              return (
                <li key={e.id} className="card-soft bg-white p-6 flex items-center gap-5" data-testid={`calendar-page-item-${i}`}>
                  <div className="w-20 h-20 rounded-2xl bg-brand-blue text-white flex flex-col items-center justify-center flex-shrink-0">
                    <div className="text-xs uppercase tracking-wider opacity-85">{d.toLocaleString("en", { month: "short" })}</div>
                    <div className="font-heading text-3xl font-bold leading-none">{d.getDate()}</div>
                    <div className="text-[0.65rem] opacity-80 mt-0.5">{d.getFullYear()}</div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-heading font-bold text-lg">{e.title}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${typeColor[e.event_type] || typeColor.event}`}>
                        {e.event_type}
                      </span>
                    </div>
                    {e.description && (
                      <p className="text-sm text-[color:var(--text-secondary)]">{e.description}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Lightbox */}
      {showPoster && poster && (
        <div
          className="fixed inset-0 z-[60] bg-[color:rgba(15,29,58,0.85)] backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowPoster(false)}
          data-testid="calendar-poster-lightbox"
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/95 hover:bg-white flex items-center justify-center"
            onClick={() => setShowPoster(false)}
            aria-label="Close"
            data-testid="calendar-poster-lightbox-close"
          >
            <X size={20} />
          </button>
          <img
            src={posterSrc}
            alt={poster.label}
            className="max-w-full max-h-[92vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </main>
  );
};

export default CalendarPage;
